import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money, hrs, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useTimer } from "@/context/TimerContext";
import { useAuth } from "@/context/AuthContext";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import {
  Receipt, Clock, Wallet, TrendingUp, Plus, Square, AlertTriangle, ArrowRight,
} from "lucide-react";

const Kpi = ({ label, value, meta, icon: Icon, tone = "default", testid }) => {
  const toneColor = {
    default: "text-foreground",
    warning: "text-[color:var(--kpi-warning)]",
    danger: "text-[color:var(--kpi-danger)]",
    positive: "text-[color:var(--kpi-positive)]",
  }[tone];
  return (
    <Card className="p-4 sm:p-5 shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow">
      <div className="flex items-start justify-between">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <div className="h-8 w-8 rounded-lg bg-accent flex items-center justify-center"><Icon className="h-4 w-4 text-primary" /></div>
      </div>
      <div className={`text-2xl sm:text-3xl font-semibold tabular-nums tracking-tight mt-2 font-display ${toneColor}`} data-testid={testid}>{value}</div>
      {meta && <div className="text-xs text-muted-foreground mt-1">{meta}</div>}
    </Card>
  );
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const { active, fmtElapsed, clockOut } = useTimer();
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [s, t] = await Promise.all([api.get("/dashboard/summary"), api.get("/dashboard/trends")]);
      setData(s.data);
      setTrends(t.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-display">Good to see you, {user?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-muted-foreground mt-1">Here’s your business at a glance.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="dashboard-quick-log-time-button" onClick={() => navigate("/timesheets")}><Clock className="h-4 w-4 mr-2" />Log time</Button>
          <Button data-testid="dashboard-quick-new-invoice-button" onClick={() => navigate("/invoices?new=1")}><Plus className="h-4 w-4 mr-2" />New invoice</Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[0,1,2,3].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Kpi label="Unpaid invoices" value={money(data.unpaid_total)} meta={`${data.counts.unpaid_invoices} outstanding${data.overdue_total > 0 ? ` · ${money(data.overdue_total)} overdue` : ""}`} icon={Receipt} tone={data.overdue_total > 0 ? "danger" : "warning"} testid="kpi-unpaid-invoices-total" />
          <Kpi label="This week" value={hrs(data.week_hours)} meta="Hours logged (Mon–Sun)" icon={Clock} testid="kpi-current-week-hours-total" />
          <Kpi label="Uninvoiced value" value={money(data.uninvoiced_value)} meta="Billable, not yet invoiced" icon={Wallet} tone="positive" testid="kpi-uninvoiced-value-total" />
          <Kpi label="Projected / month" value={money(data.projected_monthly)} meta={`${money(data.projected_weekly)} / week`} icon={TrendingUp} testid="kpi-projected-earnings-total" />
        </div>
      )}

      {active && (
        <Card className="p-4 sm:p-5 border-l-4 border-l-primary" data-testid="running-timer-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="h-2.5 w-2.5 rounded-full bg-primary animate-pulse" />
              <div>
                <div className="text-sm text-muted-foreground">Currently tracking</div>
                <div className="font-semibold font-display">{active.job_title}</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-mono text-2xl font-semibold tabular-nums">{fmtElapsed()}</span>
              <Button variant="destructive" data-testid="dashboard-clock-out-button" onClick={async () => { await clockOut(); load(); }}><Square className="h-4 w-4 mr-2" />Clock out</Button>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <Card className="lg:col-span-2 p-4 sm:p-5" data-testid="dashboard-hours-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold font-display">Hours & income</h3>
              <p className="text-xs text-muted-foreground">Last 8 weeks</p>
            </div>
          </div>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trends?.weeks || []} margin={{ left: -18, right: 6, top: 6, bottom: 0 }}>
                <defs>
                  <linearGradient id="gHours" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(196 84% 32%)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(196 84% 32%)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", color: "hsl(var(--card-foreground))", fontSize: 12 }}
                  formatter={(v, name) => name === "income" ? [money(v), "Income"] : [`${v} hrs`, "Hours"]} />
                <Area type="monotone" dataKey="hours" stroke="hsl(196 84% 32%)" strokeWidth={2.5} fill="url(#gHours)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-4 sm:p-5" data-testid="dashboard-invoice-chart">
          <h3 className="text-lg font-semibold font-display mb-1">Invoice value</h3>
          <p className="text-xs text-muted-foreground mb-4">By status</p>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trends?.status_breakdown || []} margin={{ left: -18, right: 6, top: 6, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="status" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: "hsl(var(--accent))" }} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", color: "hsl(var(--card-foreground))", fontSize: 12 }} formatter={(v) => [money(v), "Value"]} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {(trends?.status_breakdown || []).map((_, i) => (
                    <Cell key={i} fill={["#94a3b8", "#b45309", "#0f766e"][i]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <Card className="lg:col-span-2 p-0 overflow-hidden">
          <div className="flex items-center justify-between p-4 sm:p-5 border-b">
            <h3 className="text-lg font-semibold font-display">Outstanding invoices</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/invoices")}>View all <ArrowRight className="h-4 w-4 ml-1" /></Button>
          </div>
          {(!data || data.recent_unpaid.length === 0) ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No outstanding invoices. Nice and clean!</div>
          ) : (
            <div className="divide-y">
              {data.recent_unpaid.map((inv) => {
                const overdue = inv.due_date && inv.due_date < new Date().toISOString().slice(0,10);
                return (
                  <div key={inv.id} className="flex items-center justify-between p-4 hover:bg-accent/40 transition-colors" data-testid={`dashboard-invoice-row-${inv.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium">{inv.invoice_number}</span>
                        <Badge variant="outline" className="capitalize">{inv.status}</Badge>
                        {overdue && <Badge className="bg-[color:var(--kpi-danger)] text-white gap-1"><AlertTriangle className="h-3 w-3" />Overdue</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">{inv.job_title} · due {fmtDate(inv.due_date)}</div>
                    </div>
                    <div className="font-semibold tabular-nums">{money(inv.total_amount)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-4 sm:p-5">
          <h3 className="text-lg font-semibold font-display mb-4">Quick stats</h3>
          <div className="space-y-3">
            {data && [
              { label: "Clients", value: data.counts.clients },
              { label: "Active jobs", value: data.counts.active_jobs },
              { label: "Unpaid invoices", value: data.counts.unpaid_invoices },
            ].map((s) => (
              <div key={s.label} className="flex items-center justify-between rounded-lg border bg-secondary/50 px-3 py-2.5">
                <span className="text-sm text-muted-foreground">{s.label}</span>
                <span className="font-semibold tabular-nums">{s.value}</span>
              </div>
            ))}
            <Button variant="outline" className="w-full mt-2" onClick={() => navigate("/forecast")}><TrendingUp className="h-4 w-4 mr-2" />Open forecaster</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
